import { Avatar, Box, Button, Card, CardContent, Grid, List, Theme, Typography, TextField } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react'
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import StudentProfileImage from '../../../../../../Assets/student-profile.jpg';
import { makeStyles } from '@mui/styles';
import { updateStudentProfile, getStudentProfile } from '../../../../../../Utils/apiService';
import KenLoader from '../../../../../../GlobalComponents/KenLoader';
const useStyles = makeStyles((theme: Theme) => ({
    extraCuricularBox2: {
        borderRadius: '12px !important',
        height: '49.3vh !important',
        '@media (min-width: 1440px) and (max-width: 1500px)': {
            height: '52vh !important',
        },
        '@media (min-width: 1920px) and (max-width: 1940px)': {
            height: '54vh !important',
        },
        '@media (min-width: 1280px) and (max-width: 1300px)': {
            padding: '10px !important',
            height: '43.8vh !important',
        },
    },
    categoriesTitle: {
        fontSize: '15px !important',
        textAlign: 'left',
        fontWeight: '500 !important',
        '@media (min-width: 1280px) and (max-width: 1300px)': {
            fontSize: '12px !important',
        },
    },
    categoriesBox: {
        backgroundColor: '#FFFFFF !important',
        borderRadius: '12px !important',
        padding: '5px !important',
        marginTop: '10px !important',
        '@media (min-width: 1280px) and (max-width: 1300px)': {
            marginTop: '5px !important',
            padding: '5px !important',
        },
    },
    clubsBox: {
        backgroundColor: '#FFFFFF !important',
        borderRadius: '12px !important',
        padding: '16px !important',
        border: '0.5px solid #BFCEFF',
        marginTop: '10px !important',
    },
    AddButton: {
        background: 'linear-gradient(90deg, #3061FF -15.54%, #30C1FF 133.56%)',
        color: '#FFFFFF !important',
        borderRadius: '8px !important',
    },
    clubsTitle: {
        fontSize: '14px !important',
        textAlign: 'left',
        fontWeight: '500 !important',
        '@media (min-width: 1280px) and (max-width: 1300px)': {
            fontSize: '11px !important',
        },
    },
    clubsContent: {
        fontSize: '12px !important',
        textAlign: 'left',
        fontWeight: '400 !important',
        '@media (min-width: 1280px) and (max-width: 1300px)': {
            fontSize: '10px !important',
        },
    },
    td: {
        fontSize: '12px !important',
        fontWeight: '400 !important',
        textAlign: 'left',
        padding: '10px !important',
        '@media (min-width: 1280px) and (max-width: 1300px)': {
            fontSize: '11px !important',
        },
    },
}));

const cardData = [
    {
        title: 'Learning Preferences',
        color: '#E6F4EA',
        titleColor: 'success.main',
        items: [
            'Learns best through visual aids, diagrams, and hands-on activities.',
            'Prefers working in small groups where ideas can be discussed.',
            'Finds it easier to grasp concepts when real-world applications are shown.',
            'Needs extra time during assessments, especially when writing essays.',
            'Enjoys fast-paced learning in tech-related subjects but prefers guided support in.',
        ],
    },
    {
        title: 'Growth Areas',
        color: '#E8EDFF',
        titleColor: 'primary.main',
        items: [
            'Needs structured help with organizing assignments and deadlines.',
            'Working on building confidence in public speaking and class participation.',
            'Struggles with multi-step math problems; extra support in Algebra is helpful.',
            'Developing emotional resilience when handling peer feedback.',
            'Aiming to improve time management during exam prep.',
        ],
    },
    {
        title: 'Interests',
        color: '#F4EDFB',
        titleColor: 'secondary.main',
        items: [
            'Enjoys exploring science experiments, especially chemistry and biology.',
            'Passionate about sketching, painting, and digital art.',
            'Plays basketball and participates in school tournaments.',
            'Interested in technology and has begun learning Python.',
        ],
    },
    {
        title: 'Future Aspirations',
        color: '#FFF8E1',
        titleColor: 'warning.main',
        items: [
            'Aspires to become a biomedical engineer and work in medical innovation.',
            'Wants to attend a top-tier university like MIT or Stanford.',
            'Hopes to start a YouTube channel teaching science concepts to kids.',
            'Dreams of representing the school in a national robotics competition.',
        ],
    },
];

const AboutMe = (props: any) => {
    const [isLoading, setIsLoading] = useState(false);
    const classes = useStyles();
    const [isedit, setIsEdit] = useState(false);
    const [editableData, setEditableData] = useState(cardData);

    // Function to parse HTML content and convert to array of items
    const parseHtmlContent = (htmlContent: string) => {
        if (!htmlContent) return [];
        // Split by both semicolons and line breaks, then filter out empty lines
        return htmlContent
            .split(/[;\r\n]+/) // Split by semicolon, carriage return, or newline
            .map(item => item.trim())
            .filter(item => item.length > 0);
    };

    // Function to calculate card height based on content
    const calculateCardHeight = (items: string[]) => {
        const baseHeight = 120; // Base height for title and padding
        const itemHeight = 20; // Height per item
        const maxItems = 5; // Maximum items to show

        const displayItems = items.slice(0, maxItems);
        return baseHeight + (displayItems.length * itemHeight);
    };

    // Function to truncate text if it's too long
    const truncateText = (text: string, maxLength: number = 80) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Calculate heights for card groups
    const cardHeights = useMemo(() => {
        if (!editableData || editableData.length < 4) return { topHeight: 200, bottomHeight: 200 };

        const topCards = [editableData[0], editableData[1]]; // Learning Preferences, Growth Areas
        const bottomCards = [editableData[2], editableData[3]]; // Interests, Future Aspirations

        // Calculate height based on the first card's content (Learning Preferences)
        const firstCardItems = topCards[0]?.items || [];
        const topHeight = calculateCardHeight(firstCardItems);

        // Calculate height based on the third card's content (Interests)
        const thirdCardItems = bottomCards[0]?.items || [];
        const bottomHeight = calculateCardHeight(thirdCardItems);

        return { topHeight, bottomHeight };
    }, [editableData]);

    // Function to get items for display based on reference card
    const getDisplayItems = (cardIndex: number, referenceCardIndex: number) => {
        const card = editableData[cardIndex];
        const referenceCard = editableData[referenceCardIndex];

        if (!card || !referenceCard) return card?.items || [];

        const referenceItemCount = referenceCard.items.length;
        const maxItems = Math.min(referenceItemCount, 5); // Max 5 items

        return card.items.slice(0, maxItems);
    };

    // Function to create card data from API response
    const createCardDataFromAPI = (studentData: any) => {
        if (!studentData) return cardData;

        // Ensure each section has at least one empty item for editing
        const ensureEditableFields = (items: string[]) => {
            if (!items || items.length === 0) {
                return ['']; // Return one empty field for editing
            }
            return items;
        };

        return [
            {
                title: 'Learning Preferences',
                color: '#E6F4EA',
                titleColor: 'success.main',
                items: ensureEditableFields(parseHtmlContent(studentData.Learning_Preferences__c || '')),
            },
            {
                title: 'Growth Areas',
                color: '#E8EDFF',
                titleColor: 'primary.main',
                items: ensureEditableFields(parseHtmlContent(studentData.Growth_Areas__c || '')),
            },
            {
                title: 'Interests',
                color: '#F4EDFB',
                titleColor: 'secondary.main',
                items: ensureEditableFields(parseHtmlContent(studentData.Interests__c || '')),
            },
            {
                title: 'Future Aspirations',
                color: '#FFF8E1',
                titleColor: 'warning.main',
                items: ensureEditableFields(parseHtmlContent(studentData.Future_Aspirations__c || '')),
            },
        ];
    };

    // Update editable data when props change
    useEffect(() => {
        const studentData = props?.props?.props?.data?.studentDetails[0];
        if (studentData) {
            const apiCardData = createCardDataFromAPI(studentData);
            setEditableData(apiCardData);
        }
    }, [props?.props?.props?.data?.studentDetails]);

    const handleItemChange = (cardIndex: number, itemIndex: number, newValue: string) => {
        const newData = [...editableData];

        // Update the item value
        newData[cardIndex].items[itemIndex] = newValue;

        // If the field is empty and not the last item, remove it
        if (newValue.trim() === '' && newData[cardIndex].items.length > 1) {
            newData[cardIndex].items.splice(itemIndex, 1);
        }

        setEditableData(newData);
    };

    const handleItemKeyDown = (cardIndex: number, itemIndex: number, e: React.KeyboardEvent) => {
        // If user presses Enter and the current field is not empty, add a new field
        if (e.key === 'Enter' && editableData[cardIndex].items[itemIndex].trim() !== '') {
            e.preventDefault();
            const newData = [...editableData];
            newData[cardIndex].items.splice(itemIndex + 1, 0, '');
            setEditableData(newData);

            // Auto-focus the new field after a short delay
            setTimeout(() => {
                const newFieldId = `field-${cardIndex}-${itemIndex + 1}`;
                const newField = document.getElementById(newFieldId);
                if (newField) {
                    newField.focus();
                }
            }, 10);
        }

        // If user presses Backspace on empty field, remove it and focus previous field
        if (e.key === 'Backspace' && editableData[cardIndex].items[itemIndex] === '' && itemIndex > 0) {
            e.preventDefault();
            const newData = [...editableData];
            newData[cardIndex].items.splice(itemIndex, 1);
            setEditableData(newData);

            // Auto-focus the previous field
            setTimeout(() => {
                const prevFieldId = `field-${cardIndex}-${itemIndex - 1}`;
                const prevField = document.getElementById(prevFieldId);
                if (prevField) {
                    prevField.focus();
                }
            }, 10);
        }
    };

    const handleItemBlur = (cardIndex: number, itemIndex: number) => {
        // Remove empty fields when user leaves the field (except the last one)
        const newData = [...editableData];
        if (newData[cardIndex].items[itemIndex].trim() === '' &&
            newData[cardIndex].items.length > 1 &&
            itemIndex !== newData[cardIndex].items.length - 1) {
            newData[cardIndex].items.splice(itemIndex, 1);
            setEditableData(newData);
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const studentData = props?.props?.props?.data?.studentDetails[0];
            if (!studentData?.Id) {
                console.error('Contact ID not found');
                return;
            }

            // Format the data for API payload
            const formatDataForAPI = (items: string[]) => {
                return items
                    .filter(item => item.trim() !== '') // Remove empty items
                    .join(';'); // Join with semicolon
            };

            const payload = {
                Growth_Areas__c: formatDataForAPI(editableData[1]?.items || []),
                Interests__c: formatDataForAPI(editableData[2]?.items || []),
                Future_Aspirations__c: formatDataForAPI(editableData[3]?.items || []),
                Learning_Preferences__c: formatDataForAPI(editableData[0]?.items || [])
            };
            const response = await updateStudentProfile(props?.props?.props?.data?.studentDetails[0]?.Id, payload);
            console.log(response?.length,"responseresponseresponse");
            
            // Check if response has status code 204 (No Content) - check multiple possible locations
  
            if (response?.length===0) {
            console.log('Data saved successfully with status 204');

            // Refresh data after successful save
            const studentId = props?.props?.props?.data?.studentDetails[0]?.Id;
            if (studentId) {
                try {
                    const response = await getStudentProfile(studentId);
                    if (response && response.data && response.data.studentDetails && response.data.studentDetails[0]) {
                        const updatedStudentData = response.data.studentDetails[0];
                        const newCardData = createCardDataFromAPI(updatedStudentData);
                        setEditableData(newCardData);
                        setIsEdit(false);
                        setIsLoading(false);
                        console.log('Data refreshed successfully:', newCardData);
                    } else {
                        console.error('Invalid response structure from getStudentProfile');
                        setIsLoading(false);
                    }
                } catch (refreshError) {
                    console.error('Error refreshing data:', refreshError);
                    setIsLoading(false);
                }
            }
            } else {
                console.error('Save failed - unexpected response:', response);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error saving data:', error);
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        const studentData = props?.props?.props?.data?.studentDetails[0];
        if (studentData) {
            const apiCardData = createCardDataFromAPI(studentData);
            setEditableData(apiCardData);
        }
        setIsEdit(false);
    };

    return (
        <Box className={`${classes.extraCuricularBox2} scrollbar-cusgray`}>
            {isLoading && <KenLoader />}
            {isedit ? (
                <Box sx={{ p: 3 }}>
                    <Box className="d-flex justify-content-between align-items-center mb-4" style={{ float: "right" }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                onClick={handleCancel}
                                sx={{
                                    backgroundColor: '#E8EBF3',
                                    color: "#373A45",
                                    width: "100px",
                                    borderRadius: '8px',
                                    '&:hover': { backgroundColor: '#D1D5DB' }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                sx={{
                                    backgroundColor: '#3061FF',
                                    color: "#FFFFFF",
                                    width: "100px",
                                    borderRadius: '8px',
                                    '&:hover': { backgroundColor: '#1E40AF' }
                                }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>

                    {/* Edit Mode Content */}
                    <Box sx={{ maxWidth: '800px' }}>
                        {editableData.map((card, cardIndex) => (
                            <Box key={card.title} sx={{ mb: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#373A45',
                                        fontSize: '16px',
                                        fontWeight: '400 !important',
                                        textAlign: 'left',
                                        marginTop: '20px',
                                        marginBottom: '10px',
                                    }}
                                >
                                    {card.title}
                                </Typography>
                                <Box
                                    sx={{
                                        border: cardIndex === 0 ? '2px solid #3061FF' : '2px solid #E0E0E0',
                                        borderRadius: '8px',
                                        p: 3,
                                        backgroundColor: '#FFFFFF'
                                    }}
                                >
                                    {card.items.map((item, itemIndex) => (
                                        <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                            <Typography sx={{
                                                fontSize: '14px',
                                                color: '#666',
                                                mr: 2,
                                                mt: 0.5,
                                                flexShrink: 0
                                            }}>
                                                •
                                            </Typography>
                                            <TextField
                                                id={`field-${cardIndex}-${itemIndex}`}
                                                value={item}
                                                onChange={(e) => handleItemChange(cardIndex, itemIndex, e.target.value)}
                                                onKeyDown={(e) => handleItemKeyDown(cardIndex, itemIndex, e)}
                                                onBlur={() => handleItemBlur(cardIndex, itemIndex)}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                multiline
                                                placeholder={itemIndex === card.items.length - 1 ? "Add new item..." : ""}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: 'transparent',
                                                        fontSize: '14px',
                                                        '& fieldset': {
                                                            borderColor: 'transparent',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'transparent',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'transparent',
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}

                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                <>
                    <Box className="d-flex justify-content-end align-items-center">
                        <Button onClick={() => setIsEdit(true)} sx={{ backgroundColor: '#E8EBF3', color: "#373A45", width: "100px" }}><ModeEditIcon sx={{ color: "#373A45" }} />Edit</Button>
                    </Box>
                    <Box sx={{ p: 1, maxWidth: '1200px', mx: 'auto', position: 'relative', textAlign: 'left' }}>
                        <Grid container spacing={4}>
                            {editableData.map((card, index) => {
                                const isTopCard = index < 2;
                                const cardHeight = isTopCard ? cardHeights.topHeight : cardHeights.bottomHeight;

                                return (
                                    <Grid key={card.title} item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                backgroundColor: card.color,
                                                borderRadius: 3,
                                                margin: "50px 28px",
                                                height: cardHeight,
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <Typography color={card.titleColor} gutterBottom sx={{ fontWeight: 500 }}>
                                                    {card.title}
                                                </Typography>
                                                <List dense sx={{ flex: 1, overflow: 'hidden' }}>
                                                    {getDisplayItems(index, index < 2 ? 0 : 2).map((item, i) => (
                                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                                            <Typography sx={{
                                                                fontSize: '12px',
                                                                color: '#666',
                                                                mr: 1,
                                                                mt: 0.5,
                                                                flexShrink: 0
                                                            }}>
                                                                •
                                                            </Typography>
                                                            <Typography
                                                                fontSize={"12px"}
                                                                lineHeight={"200%"}
                                                                sx={{ flex: 1 }}
                                                            >
                                                                {truncateText(item)}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}

                            {/* Profile Avatar Center */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 10,
                                }}
                            >
                                <Avatar
                                    src={StudentProfileImage}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        boxShadow: 3,
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Box>
                </>
            )}
        </Box>
    )
}

export default AboutMe